import type {
  IChartApi,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesApi,
  ISeriesPrimitive,
  SeriesAttachedParameter,
  SeriesType,
  Time,
} from 'lightweight-charts';
import { snapPrice, type SymbolPrecision } from '../chart/chartTrading';
import {
  POINTS_NEEDED,
  TimeAxis,
  anchorsOf,
  createDrawing,
  distanceToShapes,
  moveAnchor,
  shapesOf,
  translate,
  type Anchor,
  type Drawing,
  type Mapper,
  type Shape,
  type Tool,
} from './model';
import { barAt, magnetPrice, measure, type Ohlc } from './measure';

/** The tools the layer can run: the saved drawings, and the measure tool, whose result is shown but never saved. */
export type ActiveTool = Tool | 'measure';
const pointsFor = (tool: ActiveTool) => (tool === 'measure' ? 2 : POINTS_NEEDED[tool]);

export interface MeasureColors {
  up: string;
  down: string;
  text: string;
  panel: string;
}

/** How close, in pixels, the pointer must be to a drawing to grab it, and to a handle to pick it. */
const GRAB = 6;
const HANDLE = 5;

type DrawTarget = Parameters<IPrimitivePaneRenderer['draw']>[0];

export interface DrawingHandlers {
  /** The drawings changed (created, moved, deleted): the full new list for this symbol. */
  onChange: (list: Drawing[]) => void;
  onSelect: (id: string | null) => void;
  /** A drawing was placed, or the placing was cancelled: the tool goes back to the pointer. */
  onToolDone: () => void;
  /** A short instruction for what to click next, or null. */
  onHint: (text: string | null) => void;
}

const dash = (style: Drawing['style']) => (style === 'dashed' ? [7, 4] : style === 'dotted' ? [2, 3] : []);

class Renderer implements IPrimitivePaneRenderer {
  constructor(private readonly layer: DrawingLayer) {}
  draw(target: DrawTarget) {
    target.useMediaCoordinateSpace(({ context, mediaSize }) => {
      const mapper = this.layer.mapper(mediaSize.width, mediaSize.height);
      if (!mapper) return;
      context.save();
      context.beginPath();
      context.rect(0, 0, mediaSize.width, mediaSize.height);
      context.clip();
      for (const d of this.layer.visible()) this.drawOne(context, d, mapper);
      const span = this.layer.measureSpan();
      if (span) this.drawMeasure(context, span[0], span[1], mapper);
      context.restore();
    });
  }

  private drawOne(ctx: CanvasRenderingContext2D, d: Drawing, mapper: Mapper) {
    const selected = d.id === this.layer.selectedId;
    const shapes = shapesOf(d, mapper);
    ctx.strokeStyle = d.color;
    ctx.fillStyle = d.color;
    ctx.lineWidth = d.width + (selected ? 1 : 0);
    ctx.setLineDash(dash(d.style));
    ctx.font = '12px sans-serif';
    for (const s of shapes) this.drawShape(ctx, s, d);
    if (selected) {
      ctx.setLineDash([]);
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = d.color;
      ctx.lineWidth = 2;
      for (const a of anchorsOf(d, mapper)) {
        ctx.beginPath();
        ctx.rect(a.x - HANDLE, a.y - HANDLE, HANDLE * 2, HANDLE * 2);
        ctx.fill();
        ctx.stroke();
      }
    }
  }

  private drawMeasure(ctx: CanvasRenderingContext2D, a: Anchor, b: Anchor, mapper: Mapper) {
    const [x1, y1, x2, y2] = [mapper.x(a.t), mapper.y(a.p), mapper.x(b.t), mapper.y(b.p)];
    if (x1 === null || y1 === null || x2 === null || y2 === null) return;
    const result = this.layer.measurement(a, b);
    const colors = this.layer.measureColors;
    const color = result.up ? colors.up : colors.down;
    ctx.save();
    ctx.setLineDash([]);
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.15;
    ctx.fillRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
    ctx.globalAlpha = 1;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    // The read-out, next to the end point and kept inside the chart.
    ctx.font = '12px sans-serif';
    const width = Math.max(...result.lines.map((l) => ctx.measureText(l).width)) + 12;
    const height = result.lines.length * 16 + 8;
    const bx = Math.min(Math.max(4, x2 + 8), mapper.width - width - 4);
    const by = Math.min(Math.max(4, y2 - height / 2), mapper.height - height - 4);
    ctx.fillStyle = colors.panel;
    ctx.globalAlpha = 0.95;
    ctx.fillRect(bx, by, width, height);
    ctx.globalAlpha = 1;
    ctx.strokeRect(bx, by, width, height);
    ctx.fillStyle = colors.text;
    result.lines.forEach((line, i) => ctx.fillText(line, bx + 6, by + 18 + i * 16));
    ctx.restore();
  }

  private drawShape(ctx: CanvasRenderingContext2D, s: Shape, d: Drawing) {
    if (s.kind === 'line') {
      ctx.beginPath();
      ctx.moveTo(s.x1, s.y1);
      ctx.lineTo(s.x2, s.y2);
      ctx.stroke();
      if (s.arrow) {
        const angle = Math.atan2(s.y2 - s.y1, s.x2 - s.x1);
        const size = 10 + d.width * 2;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(s.x2, s.y2);
        ctx.lineTo(s.x2 - size * Math.cos(angle - 0.4), s.y2 - size * Math.sin(angle - 0.4));
        ctx.lineTo(s.x2 - size * Math.cos(angle + 0.4), s.y2 - size * Math.sin(angle + 0.4));
        ctx.closePath();
        ctx.fill();
        ctx.setLineDash(dash(d.style));
      }
      if (s.label) {
        ctx.save();
        ctx.setLineDash([]);
        ctx.fillText(s.label, Math.min(s.x1, s.x2) + 4, s.y1 - 3);
        ctx.restore();
      }
    } else if (s.kind === 'rect') {
      ctx.save();
      ctx.globalAlpha = 0.15;
      ctx.fillRect(s.x, s.y, s.w, s.h);
      ctx.restore();
      ctx.strokeRect(s.x, s.y, s.w, s.h);
    } else if (s.kind === 'fill') {
      ctx.save();
      ctx.globalAlpha = 0.1;
      ctx.beginPath();
      s.points.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    } else if (s.kind === 'ellipse') {
      if (s.rx <= 0 || s.ry <= 0) return;
      ctx.beginPath();
      ctx.ellipse(s.cx, s.cy, s.rx, s.ry, 0, s.from, s.to);
      if (s.fill) {
        ctx.save();
        ctx.globalAlpha = 0.1;
        ctx.fill();
        ctx.restore();
      }
      ctx.stroke();
    } else {
      ctx.save();
      ctx.setLineDash([]);
      ctx.font = '13px sans-serif';
      if (s.boxed) {
        // a price label: the price in a box of the drawing's colour, pointing at its level
        const w = ctx.measureText(s.text).width + 10;
        ctx.globalAlpha = 0.9;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x + 6, s.y - 9);
        ctx.lineTo(s.x + 6 + w, s.y - 9);
        ctx.lineTo(s.x + 6 + w, s.y + 9);
        ctx.lineTo(s.x + 6, s.y + 9);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#ffffff';
        ctx.fillText(s.text, s.x + 11, s.y + 4);
      } else {
        ctx.fillText(s.text, s.x, s.y);
      }
      ctx.restore();
    }
  }
}

class Primitive implements ISeriesPrimitive<Time> {
  private update: (() => void) | null = null;
  private readonly view: IPrimitivePaneView;
  constructor(layer: DrawingLayer) {
    this.view = {
      zOrder: () => 'top',
      renderer: () => new Renderer(layer),
    };
  }
  attached(param: SeriesAttachedParameter<Time>) {
    this.update = param.requestUpdate;
  }
  detached() {
    this.update = null;
  }
  updateAllViews() {}
  paneViews() {
    return [this.view];
  }
  redraw() {
    this.update?.();
  }
}

/**
 * Draws the user's drawings on the chart and lets them be created, selected, moved and deleted. It draws with a series
 * primitive (so it is always in step with the chart's scales) and handles pointer events itself, in the capture phase like
 * the trade-line layer, which has priority: grabbing a trade line never starts a drawing.
 */
export class DrawingLayer {
  private drawings: Drawing[] = [];
  private tool: ActiveTool | null = null;
  private bars: (Ohlc & { t: number })[] = [];
  private magnet = false;
  private measured: [Anchor, Anchor] | null = null;
  measureColors: MeasureColors = { up: '#26a69a', down: '#ef5350', text: '#ffffff', panel: '#1e1e2e' };
  private colorForNew = '#f5a623';
  private precision: SymbolPrecision = { digits: 5, tickSize: 0.00001 };
  private handlers!: DrawingHandlers;
  private axis = new TimeAxis([], 60);
  private canUseKeys: () => boolean = () => true;
  selectedId: string | null = null;

  private pending: Anchor[] = [];
  private cursor: Anchor | null = null;
  private drag:
    | { kind: 'body'; id: string; from: Anchor; origin: Drawing }
    | { kind: 'handle'; id: string; index: number; origin: Drawing }
    | null = null;
  private working: Drawing | null = null;
  private primitive = new Primitive(this);
  private attachedTo: ISeriesApi<SeriesType> | null = null;

  constructor(
    private readonly host: HTMLElement,
    private readonly chart: IChartApi,
    private readonly getSeries: () => ISeriesApi<SeriesType> | null,
  ) {
    host.addEventListener('pointerdown', this.onPointerDown, true);
    host.addEventListener('mousedown', this.onMouseDown, true);
    window.addEventListener('pointerup', this.onAnyUp, true);
    host.addEventListener('pointermove', this.onHover);
    window.addEventListener('keydown', this.onKey);
    (host as HTMLElement & { __adioxDrawings?: () => unknown }).__adioxDrawings = () => this.debug();
  }

  destroy() {
    this.host.removeEventListener('pointerdown', this.onPointerDown, true);
    this.host.removeEventListener('mousedown', this.onMouseDown, true);
    window.removeEventListener('pointerup', this.onAnyUp, true);
    this.host.removeEventListener('pointermove', this.onHover);
    window.removeEventListener('keydown', this.onKey);
    this.stopDrag();
    this.detach();
  }

  private detach() {
    if (this.attachedTo) {
      try {
        this.attachedTo.detachPrimitive(this.primitive);
      } catch {
        // the series may already be gone
      }
      this.attachedTo = null;
    }
  }

  update(
    drawings: Drawing[],
    selectedId: string | null,
    tool: ActiveTool | null,
    colorForNew: string,
    bars: (Ohlc & { t: number })[],
    step: number,
    precision: SymbolPrecision,
    canUseKeys: () => boolean,
    handlers: DrawingHandlers,
    options: { magnet: boolean; measureColors: MeasureColors },
  ) {
    this.drawings = drawings;
    this.selectedId = selectedId;
    this.colorForNew = colorForNew;
    if (bars !== this.bars)
      this.axis = new TimeAxis(
        bars.map((b) => b.t),
        step,
      );
    this.bars = bars;
    this.magnet = options.magnet;
    this.measureColors = options.measureColors;
    this.precision = precision;
    this.canUseKeys = canUseKeys;
    this.handlers = handlers;
    if (tool !== this.tool) {
      this.tool = tool;
      this.pending = [];
      this.cursor = null;
      this.announce();
    }
    const series = this.getSeries();
    if (series && series !== this.attachedTo) {
      this.detach();
      series.attachPrimitive(this.primitive);
      this.attachedTo = series;
    }
    this.primitive.redraw();
  }

  // ------------------------------------------------------------------ what is drawn
  /** The drawings to draw right now: the stored ones (one possibly being dragged) and the one being placed. */
  visible(): Drawing[] {
    const list = this.working
      ? this.drawings.map((d) => (d.id === this.working!.id ? this.working! : d))
      : this.drawings;
    if (this.tool && this.tool !== 'measure' && this.pending.length > 0 && this.cursor) {
      const points = [...this.pending, this.cursor];
      while (points.length < POINTS_NEEDED[this.tool]) points.push(this.cursor);
      return [...list, { ...createDrawing(this.tool, points, this.colorForNew), id: 'preview' }];
    }
    return list;
  }

  /** The span the measure tool shows: the finished one, or the one being measured. */
  measureSpan(): [Anchor, Anchor] | null {
    if (this.tool === 'measure' && this.pending.length > 0 && this.cursor)
      return [this.pending[0]!, this.cursor];
    return this.measured;
  }

  measurement(a: Anchor, b: Anchor) {
    return measure(a, b, this.precision.digits, this.axis.logicalOf(b.t) - this.axis.logicalOf(a.t));
  }

  mapper(width: number, height: number): Mapper | null {
    const series = this.getSeries();
    if (!series) return null;
    const scale = this.chart.timeScale();
    return {
      width,
      height,
      x: (t) => scale.logicalToCoordinate(this.axis.logicalOf(t) as never),
      y: (p) => series.priceToCoordinate(p),
      time: (x) => {
        const logical = scale.coordinateToLogical(x);
        return logical === null ? null : this.axis.timeOf(logical);
      },
      price: (y) => series.coordinateToPrice(y),
      bars: this.bars,
    };
  }

  private box() {
    const box = this.host.getBoundingClientRect();
    return {
      box,
      right: box.width - this.chart.priceScale('right').width(),
      bottom: box.height - this.chart.timeScale().height(),
    };
  }

  private anchorAt(x: number, y: number): Anchor | null {
    const m = this.mapper(0, 0);
    const t = m?.time(x);
    const p = m?.price(y);
    if (t === null || t === undefined || p === null || p === undefined) return null;
    if (this.magnet) {
      // The magnet: the nearest bar's time, and its open, high, low or close when the pointer is close to one.
      const logical = this.chart.timeScale().coordinateToLogical(x);
      const bar =
        logical === null
          ? barAt(this.bars, t)
          : (this.bars[Math.min(this.bars.length - 1, Math.max(0, Math.round(logical)))] ?? null);
      if (bar) {
        const series = this.getSeries();
        const snapped = series ? magnetPrice(bar, y, (price) => series.priceToCoordinate(price)) : null;
        return { t: bar.t, p: snapPrice(snapped ?? p, this.precision) };
      }
    }
    return { t, p: snapPrice(p, this.precision) };
  }

  private inPlot(x: number, y: number) {
    const { right, bottom } = this.box();
    return x >= 0 && x <= right && y >= 0 && y <= bottom;
  }

  /** The drawing and, for a selected one, the handle under the pointer. */
  private hit(x: number, y: number): { id: string; handle: number | null } | null {
    const { box } = this.box();
    const m = this.mapper(box.width, box.height);
    if (!m) return null;
    const selected = this.drawings.find((d) => d.id === this.selectedId);
    if (selected) {
      const anchors = anchorsOf(selected, m);
      const index = anchors.findIndex(
        (a) => Math.abs(a.x - x) <= HANDLE + 2 && Math.abs(a.y - y) <= HANDLE + 2,
      );
      if (index >= 0) return { id: selected.id, handle: index };
    }
    let best: { id: string; distance: number } | null = null;
    for (const d of [...this.drawings].reverse()) {
      const distance = distanceToShapes(shapesOf(d, m), x, y);
      if (distance <= GRAB && (!best || distance < best.distance)) best = { id: d.id, distance };
    }
    return best ? { id: best.id, handle: null } : null;
  }

  // ------------------------------------------------------------------ pointer handling
  private announce() {
    if (!this.tool) return this.handlers?.onHint(null);
    if (this.tool === 'measure') {
      return this.handlers?.onHint(
        this.pending.length === 0
          ? 'Click where the measurement starts. Esc cancels.'
          : 'Click where it ends. Esc cancels.',
      );
    }
    const need = POINTS_NEEDED[this.tool];
    const next = this.pending.length + 1;
    this.handlers?.onHint(
      need === 1
        ? 'Click on the chart to place it. Esc cancels.'
        : `Click point ${next} of ${need} on the chart. Esc cancels.`,
    );
  }

  private readonly onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0 || e.defaultPrevented) return;
    const { box } = this.box();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    if (!this.inPlot(x, y)) return;
    if (this.measured && this.tool !== 'measure') {
      this.measured = null; // a finished measurement stays until the next click
      this.primitive.redraw();
    }

    if (this.tool) {
      const anchor = this.anchorAt(x, y);
      if (!anchor) return;
      e.stopImmediatePropagation();
      e.preventDefault();
      this.armed = true;
      this.pending.push(anchor);
      if (this.tool === 'measure' && this.pending.length >= 2) {
        this.measured = [this.pending[0]!, this.pending[1]!];
        this.pending = [];
        this.cursor = null;
        this.tool = null;
        this.handlers.onToolDone();
        this.handlers.onHint(null);
      } else if (this.tool !== 'measure' && this.pending.length >= pointsFor(this.tool)) {
        const made = createDrawing(this.tool, this.pending, this.colorForNew);
        this.pending = [];
        this.cursor = null;
        this.tool = null;
        this.handlers.onChange([...this.drawings, made]);
        this.handlers.onSelect(made.id);
        this.handlers.onToolDone();
        this.handlers.onHint(null);
      } else {
        this.announce();
      }
      this.primitive.redraw();
      return;
    }

    const hit = this.hit(x, y);
    if (!hit) {
      if (this.selectedId) this.handlers.onSelect(null);
      return;
    }
    e.stopImmediatePropagation();
    e.preventDefault();
    this.armed = true;
    const origin = this.drawings.find((d) => d.id === hit.id)!;
    this.handlers.onSelect(hit.id);
    this.selectedId = hit.id;
    const from = this.anchorAt(x, y);
    if (!from) return;
    this.drag =
      hit.handle !== null
        ? { kind: 'handle', id: hit.id, index: hit.handle, origin }
        : { kind: 'body', id: hit.id, from, origin };
    this.working = origin;
    window.addEventListener('pointermove', this.onDragMove);
    window.addEventListener('pointerup', this.onDragUp);
    window.addEventListener('pointercancel', this.onDragCancel);
    this.host.classList.add('line-dragging');
    this.primitive.redraw();
  };

  /** The chart also listens for mouse events; a click that placed or grabbed a drawing must not pan it. */
  private armed = false;
  private readonly onMouseDown = (e: MouseEvent) => {
    if (this.armed) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  };
  private readonly onAnyUp = () => {
    // Cleared after the mouse events of this click have passed.
    setTimeout(() => (this.armed = false), 0);
  };

  private readonly onHover = (e: PointerEvent) => {
    const { box } = this.box();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    if (this.tool) {
      if (this.pending.length > 0) {
        this.cursor = this.anchorAt(x, y);
        this.primitive.redraw();
      }
      this.host.classList.add('drawing-tool');
      return;
    }
    this.host.classList.remove('drawing-tool');
    if (this.drag) return;
    this.host.classList.toggle('drawing-hover', this.inPlot(x, y) && this.hit(x, y) !== null);
  };

  private readonly onDragMove = (e: PointerEvent) => {
    if (!this.drag) return;
    const { box } = this.box();
    const now = this.anchorAt(e.clientX - box.left, e.clientY - box.top);
    if (!now) return;
    this.working =
      this.drag.kind === 'handle'
        ? moveAnchor(this.drag.origin, this.drag.index, now)
        : translate(this.drag.origin, now.t - this.drag.from.t, now.p - this.drag.from.p);
    this.primitive.redraw();
  };

  private readonly onDragUp = () => {
    const done = this.working;
    this.stopDrag();
    if (done) {
      const changed =
        JSON.stringify(done.points) !== JSON.stringify(this.drawings.find((d) => d.id === done.id)?.points);
      if (changed) this.handlers.onChange(this.drawings.map((d) => (d.id === done.id ? done : d)));
    }
    this.primitive.redraw();
  };

  private readonly onDragCancel = () => {
    this.stopDrag();
    this.primitive.redraw();
  };

  private stopDrag() {
    this.drag = null;
    this.working = null;
    window.removeEventListener('pointermove', this.onDragMove);
    window.removeEventListener('pointerup', this.onDragUp);
    window.removeEventListener('pointercancel', this.onDragCancel);
    this.host.classList.remove('line-dragging');
  }

  private readonly onKey = (e: KeyboardEvent) => {
    if (!this.canUseKeys()) return;
    const target = e.target as HTMLElement | null;
    if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
    if (e.key === 'Escape') {
      if (this.measured) {
        this.measured = null;
        this.primitive.redraw();
      }
      if (this.drag) {
        this.stopDrag();
        this.primitive.redraw();
      } else if (this.tool) {
        this.pending = [];
        this.cursor = null;
        this.tool = null;
        this.handlers.onToolDone();
        this.handlers.onHint(null);
        this.primitive.redraw();
      } else if (this.selectedId) {
        this.handlers.onSelect(null);
      }
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedId && !this.tool) {
      e.preventDefault();
      const id = this.selectedId;
      this.handlers.onChange(this.drawings.filter((d) => d.id !== id));
      this.handlers.onSelect(null);
    }
  };

  /** For end-to-end tests: where each drawing's anchors are on screen. */
  private debug() {
    const { box } = this.box();
    const m = this.mapper(box.width, box.height);
    return this.drawings.map((d) => ({ id: d.id, tool: d.tool, anchors: m ? anchorsOf(d, m) : [] }));
  }
}
